import React, { useState, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronLeft, Pencil, Copy, X, Calendar, Coins, ShieldCheck, Tag, TrendingUp, ChevronRight, Slack, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { BrandLogo } from "@/components/BrandLogo";
import { BrandStack } from "@/components/BrandStack";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { Progress } from "@/components/ui/progress";
import { format } from "@/lib/brand-tokens";
import {
  deals, getDealById, getBrandById, getReservationsByDeal, getSubmissionsByDeal,
  getMemberById, getValidationRuleById, getAdminById, brands,
} from "@/lib/mock-data";
import { parseRateChange } from "@/lib/dataAdapter";
import { Gated } from "@/components/Gated";
import { publishDealToSlack } from "@/lib/slackClient";
import { toast } from "sonner";

// ADM-04 — Deal detail
export default function AdminDealDetailPage() {
  const { dealId } = useParams();
  const deal = getDealById(dealId) || deals[0];
  const brand = getBrandById(deal.brandId);
  const reservations = getReservationsByDeal(deal.id);
  const submissions = getSubmissionsByDeal(deal.id);
  const owner = getAdminById(deal.createdBy);
  const [slackPosting, setSlackPosting] = useState(false);
  const brandsById = useMemo(() => Object.fromEntries(brands.map(b => [b.id, b])), []);

  const handleSendToSlack = async () => {
    setSlackPosting(true);
    try {
      const r = await publishDealToSlack(deal, brandsById);
      toast.success("Posted to Slack", { description: `Channel ${r.channel} · ts=${r.ts}` });
    } catch (e) {
      toast.error("Slack post failed", { description: String(e.message || e) });
    } finally {
      setSlackPosting(false);
    }
  };

  const fillPct = Math.round((deal.capUsed / deal.capTotal) * 100);
  const cardsSubmitted = submissions.reduce((a, s) => a + s.cardCount, 0);
  const totalPayout = reservations.reduce((a, r) => a + r.expectedPayout, 0);
  const rateChange = parseRateChange(deal.internalNote);

  // Status-aware timeline. Draft deals haven't been published yet, so don't
  // fabricate a "Published" entry. Once approved/published the entry appears.
  const createdAt = new Date(new Date(deal.openedAt || Date.now()).getTime() - 2 * 86400000).toISOString();
  const approvedAt = new Date(new Date(deal.openedAt || Date.now()).getTime() - 86400000).toISOString();
  const isDraft = deal.status === "Draft";
  const timeline = isDraft
    ? [{ at: createdAt, action: "Created (Draft)", actor: owner, body: "Draft created from blank deal." }]
    : [
        deal.openedAt && { at: deal.openedAt, action: "Published", actor: owner, body: `Cap set at ${format.currency(deal.capTotal)}, rate ${deal.rate}%.` },
        { at: approvedAt, action: "Approved for review", actor: getAdminById("adm_001"), body: "Sent to publish queue." },
        { at: createdAt,  action: "Created (Draft)",     actor: owner,                    body: "Draft created from blank deal." },
      ].filter(Boolean);

  return (
    <div data-testid="admin-deal-detail-page" className="space-y-5 pb-10">
      <Breadcrumbs items={[
        { label: "Admin", to: "/admin/dashboard" },
        { label: "Deals", to: "/admin/deals" },
        { label: brand?.name || deal.name },
      ]} />

      {/* Header */}
      <div className="bg-surface border border-line rounded-xl p-6 flex flex-wrap items-start justify-between gap-6">
        <div className="flex items-center gap-4">
          <BrandStack brandIds={deal.brandIds} size="lg" />
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-brand text-h1 text-ink font-semibold">{deal.name}</h1>
              <StatusBadge entity="deal" status={deal.status} dot />
              {deal.isMultiBrand && <span className="text-caption text-brand-700 font-semibold px-2 py-0.5 rounded bg-brand-50 border border-brand-100">Multi-brand</span>}
              {deal.isFeatured && <span className="text-caption text-amber-800 font-semibold px-2 py-0.5 rounded bg-amber-50 border border-amber-200 inline-flex items-center gap-1">★ Featured</span>}
            </div>
            <div className="mt-1 text-caption text-ink-subtle">
              {deal.isMultiBrand
                ? deal.brandIds.map((id) => getBrandById(id)?.name).filter(Boolean).join(" + ")
                : brand?.name}
              {owner && <> · created by {owner.firstName} {owner.lastName}</>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link to={`/admin/deals/${deal.id}/edit`}>
            <Button variant="outline"><Pencil className="size-4 mr-1.5" /> Edit</Button>
          </Link>
          <Button variant="outline"><Copy className="size-4 mr-1.5" /> Clone</Button>
          {deal.status === "Published" && (
            <Gated action="deals.publish">
              <Button variant="outline" onClick={handleSendToSlack} disabled={slackPosting}
                      data-testid="send-to-slack-btn"
                      className="border-[#4A154B]/30 text-[#4A154B] hover:bg-[#4A154B]/5">
                {slackPosting ? <Loader2 className="size-4 mr-1.5 animate-spin" /> : <Slack className="size-4 mr-1.5" />}
                Send to Slack
              </Button>
            </Gated>
          )}
          {deal.status === "Published" && (
            <Button variant="outline" className="border-red-200 text-red-700 hover:bg-red-50">
              <X className="size-4 mr-1.5" /> Close
            </Button>
          )}
        </div>
      </div>

      {/* Retroactive rate-change banner (e.g. deal_012 — rate bumped mid-flight, existing reservations honour the new rate) */}
      {rateChange && (
        <div data-testid="rate-change-banner" className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 flex items-start gap-3">
          <TrendingUp className="size-5 text-emerald-700 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-baseline gap-2">
              <h3 className="text-h3 text-ink font-semibold">Rate bumped <span className="tabular-nums">{rateChange.from}%</span> → <span className="tabular-nums text-emerald-800">{rateChange.to}%</span></h3>
              {rateChange.retroactive && <span className="text-caption text-emerald-800 font-semibold uppercase tracking-wide">Retroactive</span>}
            </div>
            <p className="mt-1.5 text-body-sm text-ink-muted">
              {rateChange.retroactive
                ? "All existing reservations have been re-priced at the new rate. Members will see the bump in their expected payout."
                : "New reservations from this point forward use the updated rate. Earlier reservations are honoured at the original rate."}
            </p>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-5">
        {/* Main column with tabs */}
        <div className="lg:col-span-2">
          <DealTabs deal={deal} reservations={reservations} submissions={submissions} fillPct={fillPct} timeline={timeline} />
        </div>

        {/* Side */}
        <aside className="space-y-5">
          <div className="bg-surface border border-line rounded-xl p-6">
            <div className="eyebrow mb-3">Key stats</div>
            <dl className="space-y-3">
              <Stat label="Reservations" value={reservations.length} />
              <Stat label="Cards submitted" value={cardsSubmitted} />
              <Stat label="Cards approved" value={submissions.filter((s) => s.status === "Approved" || s.status === "Completed").reduce((a, s) => a + s.cardCount, 0)} />
              <Stat label="Total payout (est.)" value={format.currency(totalPayout)} accent />
              <Stat label="Cap utilisation" value={`${fillPct}%`} />
            </dl>
          </div>

          {/* Status timeline */}
          <div className="bg-surface border border-line rounded-xl p-6">
            <div className="eyebrow mb-4">Timeline</div>
            <ol className="relative border-l border-line ml-2 space-y-5">
              {timeline.map((ev, i) => (
                <li key={i} className="pl-4">
                  <span className="absolute -left-[5px] mt-1.5 size-2.5 rounded-full bg-brand-700 ring-4 ring-white" />
                  <div className="text-body-sm font-semibold text-ink">{ev.action}</div>
                  <div className="text-caption text-ink-subtle">{format.relative(ev.at)} · {ev.actor?.firstName} {ev.actor?.lastName?.[0]}.</div>
                  <div className="mt-1 text-caption text-ink-muted leading-relaxed">{ev.body}</div>
                </li>
              ))}
            </ol>
          </div>
        </aside>
      </div>
    </div>
  );
}

// ─── Tabbed sub-view ─────────────────────────────────────────────────────────
// Splits the deal detail into 3 stacked tabs. Reservations + Submissions get
// sticky-thead + max-h scroll containers so 200+ rows stay performant and
// don't push other sections off the page.

function DealTabs({ deal, reservations, submissions, fillPct, timeline }) { // eslint-disable-line no-unused-vars
  const [tab, setTab] = useState("overview");
  const tabs = [
    { id: "overview",     label: "Overview" },
    { id: "reservations", label: "Reservations", count: reservations.length },
    { id: "submissions",  label: "Submissions",  count: submissions.length },
  ];
  return (
    <div className="bg-surface border border-line rounded-xl">
      <nav role="tablist" className="border-b border-line px-2 pt-2 flex items-center gap-1" data-testid="deal-tabs">
        {tabs.map((t) => {
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              role="tab"
              aria-selected={active}
              data-testid={`deal-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`relative px-3.5 py-2.5 text-body-sm font-medium transition-colors ${active ? "text-ink" : "text-ink-muted hover:text-ink"}`}
            >
              {t.label}
              {typeof t.count === "number" && (
                <span className={`ml-2 inline-flex items-center justify-center px-1.5 min-w-[20px] h-5 rounded-full text-[11px] tabular-nums ${active ? "bg-brand-700 text-white" : "bg-surface-alt text-ink-muted"}`}>{t.count}</span>
              )}
              {active && <span className="absolute left-0 right-0 -bottom-px h-0.5 bg-brand-700 rounded-t" />}
            </button>
          );
        })}
      </nav>

      {tab === "overview"     && <OverviewTab deal={deal} reservations={reservations} fillPct={fillPct} />}
      {tab === "reservations" && <ReservationsTab reservations={reservations} />}
      {tab === "submissions"  && <SubmissionsTab submissions={submissions} />}
    </div>
  );
}

function OverviewTab({ deal, reservations, fillPct }) {
  return (
    <div className="p-6 space-y-6">
      {/* Per-brand configuration */}
      <div>
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <h2 className="text-h3 text-ink font-semibold">Configuration</h2>
          <KV icon={<Calendar className="size-3.5" />} label="Closes" value={deal.deadline ? format.date(deal.deadline) : "—"} inline />
        </div>
        <div data-testid="brand-config-table" className="border border-line rounded-md overflow-hidden">
          <div className="grid grid-cols-[1.6fr_repeat(4,1fr)_2fr] gap-x-3 px-4 py-2.5 bg-surface-alt text-[10px] font-semibold uppercase tracking-wide text-ink-hint">
            <div>Brand</div>
            <div className="text-right">We pay</div>
            <div className="text-right">Face range</div>
            <div className="text-right">Per-member</div>
            <div className="text-right">Reservations</div>
            <div>Cap usage</div>
          </div>
          <div className="divide-y divide-line/60">
            {deal.brandConfigs.map((cfg) => {
              const b = getBrandById(cfg.brandId);
              const brandResCount = reservations.filter(r => r.brandId === cfg.brandId).length;
              const cfgFillPct = cfg.capTotal > 0 ? Math.round((cfg.capUsed / cfg.capTotal) * 100) : 0;
              return (
                <div key={cfg.brandId} data-testid={`brand-config-${cfg.brandId}`}
                     className={`grid grid-cols-[1.6fr_repeat(4,1fr)_2fr] gap-x-3 px-4 py-3 items-center ${cfg.isPrimary ? "bg-brand-50/30" : ""}`}>
                  <div className="flex items-center gap-2.5 min-w-0">
                    {b && <BrandLogo brand={b} size="xs" />}
                    <div className="min-w-0">
                      <div className="text-body-sm font-semibold text-ink truncate">{b?.name || cfg.brandId}</div>
                      {cfg.isPrimary && <div className="mt-0.5"><span className="text-[10px] font-bold uppercase tracking-wide text-brand-700">Primary</span></div>}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-body-sm font-bold text-brand-700 tabular-nums">{cfg.rate}%</div>
                    <div className="text-caption text-ink-subtle tabular-nums">margin {Math.max(0, +(100 - cfg.rate).toFixed(2))}%</div>
                  </div>
                  <div className="text-right text-body-sm text-ink tabular-nums">${cfg.minFace}–${cfg.maxFace}</div>
                  <div className="text-right text-body-sm text-ink tabular-nums">{format.currency(cfg.perMemberCap)}</div>
                  <div className="text-right text-body-sm text-ink tabular-nums">{brandResCount}</div>
                  <div>
                    <div className="flex items-center justify-between text-caption mb-1">
                      <span className="text-ink-subtle tabular-nums">{format.currencyCompact(cfg.capUsed)} / {format.currencyCompact(cfg.capTotal)}</span>
                      <span className="text-ink-muted tabular-nums">{cfgFillPct}%</span>
                    </div>
                    <Progress value={cfgFillPct} className="h-1.5" />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        {deal.isMultiBrand && (
          <div className="mt-5 pt-5 border-t border-line">
            <div className="flex items-center justify-between text-caption mb-2">
              <span className="text-ink-muted">Aggregate cap usage across all brands</span>
              <span className="text-ink tabular-nums">{format.currency(deal.capUsed)} / {format.currency(deal.capTotal)} · {fillPct}%</span>
            </div>
            <Progress value={fillPct} className="h-2" />
          </div>
        )}
      </div>

      {(deal.publicNote || deal.internalNote) && (
        <div className="grid sm:grid-cols-2 gap-3 pt-2">
          {deal.publicNote && (
            <div className="bg-brand-50/50 border border-brand-100 rounded-md p-4">
              <div className="eyebrow text-brand-700 mb-1.5">Public note</div>
              <p className="text-caption text-ink-muted leading-relaxed">{deal.publicNote}</p>
            </div>
          )}
          {deal.internalNote && (
            <div className="bg-amber-50/40 border border-amber-200 rounded-md p-4">
              <div className="eyebrow text-amber-800 mb-1.5">Internal note</div>
              <p className="text-caption text-ink-muted leading-relaxed">{deal.internalNote}</p>
            </div>
          )}
        </div>
      )}

      {/* Member-facing content */}
      <div data-testid="admin-deal-content-card" className="border-t border-line pt-6">
        <h2 className="text-h3 text-ink font-semibold">Member-facing content</h2>
        <p className="mt-1 text-caption text-ink-subtle">Exactly what members see on the public deal page.</p>
        <div className="mt-5">
          <div className="eyebrow mb-2">Why we&rsquo;re buying</div>
          {deal.whyBuying ? (
            <p className="text-body-sm text-ink-muted leading-relaxed whitespace-pre-line">{deal.whyBuying}</p>
          ) : (
            <p className="text-caption text-ink-hint italic">No copy yet — edit this deal to add it.</p>
          )}
        </div>
        <div className="mt-6 pt-5 border-t border-line">
          <div className="flex items-center justify-between mb-2">
            <div className="eyebrow">FAQ shown to members</div>
            <span className="text-caption text-ink-subtle">
              {Array.isArray(deal.faqOverride) && deal.faqOverride.length > 0
                ? `${deal.faqOverride.length} custom item${deal.faqOverride.length === 1 ? "" : "s"}`
                : "Using default lifecycle FAQ"}
            </span>
          </div>
          {Array.isArray(deal.faqOverride) && deal.faqOverride.length > 0 ? (
            <ul className="space-y-3 mt-3">
              {deal.faqOverride.map((item, i) => (
                <li key={i} className="border border-line rounded-md p-3 bg-surface-alt/40">
                  <div className="text-body-sm font-semibold text-ink">{item.q}</div>
                  <p className="mt-1 text-caption text-ink-muted leading-relaxed">{item.a}</p>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-caption text-ink-hint italic mt-2">Members see the default 6-item lifecycle FAQ. Edit this deal to customise.</p>
          )}
        </div>
      </div>

      {/* Validation rules */}
      <div className="border-t border-line pt-6">
        <h2 className="text-h3 text-ink font-semibold">Validation rules <span className="text-ink-hint font-normal tabular-nums">({deal.validationRuleIds.length})</span></h2>
        <ul className="mt-4 space-y-2.5">
          {deal.validationRuleIds.map((rid) => {
            const rule = getValidationRuleById(rid);
            if (!rule) return null;
            return (
              <li key={rid} className="flex items-start gap-3 py-2">
                <span className={`mt-1.5 size-1.5 rounded-full ${rule.severity === "reject" ? "bg-red-500" : "bg-amber-500"}`} />
                <div className="flex-1">
                  <div className="text-body-sm text-ink font-medium">{rule.name}</div>
                  <div className="text-caption text-ink-subtle">{rule.description}</div>
                </div>
                <StatusBadge tone={rule.severity === "reject" ? "danger" : "warning"}>{rule.severity}</StatusBadge>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

function ReservationsTab({ reservations }) {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => reservations.filter((r) => {
    if (!q) return true;
    const m = getMemberById(r.memberId);
    const hay = `${m?.firstName} ${m?.lastName} ${r.id} ${r.status}`.toLowerCase();
    return hay.includes(q.toLowerCase());
  }), [reservations, q]);
  if (reservations.length === 0) {
    return <div className="p-12 text-center text-body-sm text-ink-muted">No reservations on this deal yet.</div>;
  }
  return (
    <div data-testid="reservations-tab">
      <div className="px-6 py-3 border-b border-line">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter by member or status…"
               className="w-full h-9 px-3 rounded-md bg-surface-alt border border-transparent text-body-sm focus:bg-surface focus:border-line outline-none" />
      </div>
      <div className="max-h-[72vh] overflow-y-auto" data-testid="reservations-scroll">
        <table className="w-full text-body-sm">
          <thead className="bg-surface-alt sticky top-0 z-[1]">
            <tr className="text-left">
              <th className="px-6 py-3 text-eyebrow uppercase text-ink-hint">Member</th>
              <th className="px-3 py-3 text-eyebrow uppercase text-ink-hint">Status</th>
              <th className="px-3 py-3 text-eyebrow uppercase text-ink-hint text-right">Reserved</th>
              <th className="px-3 py-3 text-eyebrow uppercase text-ink-hint text-right">Cards</th>
              <th className="px-6 py-3 text-eyebrow uppercase text-ink-hint text-right">Expected payout</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line/60">
            {filtered.map((r) => {
              const m = getMemberById(r.memberId);
              return (
                <tr key={r.id} className="hover:bg-surface-alt/40" data-testid={`reservation-row-${r.id}`}>
                  <td className="px-6 py-3"><Link to={`/admin/members/${m?.id}`} className="text-ink font-medium hover:text-brand-700">{m?.firstName} {m?.lastName}</Link></td>
                  <td className="px-3 py-3"><StatusBadge entity="reservation" status={r.status} /></td>
                  <td className="px-3 py-3 text-ink tabular-nums text-right">{format.currency(r.faceValueReserved)}</td>
                  <td className="px-3 py-3 text-ink-muted tabular-nums text-right">{r.cardsApproved}/{r.cardsSubmitted}</td>
                  <td className="px-6 py-3 text-brand-700 font-semibold tabular-nums text-right">{format.currency(r.expectedPayout)}</td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={5} className="px-6 py-10 text-center text-caption text-ink-subtle">No matches.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SubmissionsTab({ submissions }) {
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const counts = useMemo(() => {
    const c = { all: submissions.length, pending: 0, completed: 0, rejected: 0 };
    submissions.forEach(s => {
      if (s.status === "PendingReview" || s.status === "AwaitingMemberResponse") c.pending += 1;
      else if (s.status === "Completed" || s.status === "Approved")              c.completed += 1;
      else if (s.status === "Rejected")                                          c.rejected += 1;
    });
    return c;
  }, [submissions]);
  const filtered = useMemo(() => submissions.filter((s) => {
    if (statusFilter === "pending" && !(s.status === "PendingReview" || s.status === "AwaitingMemberResponse")) return false;
    if (statusFilter === "completed" && !(s.status === "Completed" || s.status === "Approved")) return false;
    if (statusFilter === "rejected" && s.status !== "Rejected") return false;
    if (q) {
      const m = getMemberById(s.memberId);
      const hay = `${m?.firstName} ${m?.lastName} ${s.id}`.toLowerCase();
      if (!hay.includes(q.toLowerCase())) return false;
    }
    return true;
  }), [submissions, statusFilter, q]);
  if (submissions.length === 0) {
    return <div className="p-12 text-center text-body-sm text-ink-muted">No submissions on this deal yet.</div>;
  }
  const Chip = ({ id, label, count, tone }) => {
    const active = statusFilter === id;
    const toneCls = tone === "amber" ? "bg-amber-50 text-amber-700 border-amber-200"
                  : tone === "emerald" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                  : tone === "red" ? "bg-red-50 text-red-700 border-red-200"
                  : "bg-surface-alt text-ink border-line";
    return (
      <button onClick={() => setStatusFilter(id)} data-testid={`submission-chip-${id}`}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border text-caption font-medium transition-all ${active ? "bg-ink text-white border-ink" : count > 0 ? `${toneCls} hover:shadow-sm` : "opacity-50"}`}>
        {label}<span className={`tabular-nums ${active ? "text-white/80" : "opacity-70"}`}>{count}</span>
      </button>
    );
  };
  return (
    <div data-testid="submissions-tab">
      <div className="px-6 py-3 border-b border-line flex flex-wrap items-center gap-2">
        <Chip id="all"       label="All"       count={counts.all} />
        <Chip id="pending"   label="Pending"   count={counts.pending}   tone="amber" />
        <Chip id="completed" label="Completed" count={counts.completed} tone="emerald" />
        <Chip id="rejected"  label="Rejected"  count={counts.rejected}  tone="red" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Member or ID…"
               className="flex-1 min-w-[180px] h-9 px-3 rounded-md bg-surface-alt border border-transparent text-body-sm focus:bg-surface focus:border-line outline-none" />
      </div>
      <div className="max-h-[72vh] overflow-y-auto" data-testid="submissions-scroll">
        <table className="w-full text-body-sm">
          <thead className="bg-surface-alt sticky top-0 z-[1]">
            <tr className="text-left">
              <th className="px-6 py-3 text-eyebrow uppercase text-ink-hint">Submission</th>
              <th className="px-3 py-3 text-eyebrow uppercase text-ink-hint">Member</th>
              <th className="px-3 py-3 text-eyebrow uppercase text-ink-hint">Submitted</th>
              <th className="px-3 py-3 text-eyebrow uppercase text-ink-hint text-right">Cards</th>
              <th className="px-3 py-3 text-eyebrow uppercase text-ink-hint text-right">Face</th>
              <th className="px-3 py-3 text-eyebrow uppercase text-ink-hint">Status</th>
              <th className="px-6 py-3 text-eyebrow uppercase text-ink-hint text-right"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line/60">
            {filtered.map((s) => {
              const m = getMemberById(s.memberId);
              return (
                <tr key={s.id} className="hover:bg-surface-alt/40" data-testid={`deal-submission-row-${s.id}`}>
                  <td className="px-6 py-3 font-mono text-code"><Link to={`/admin/submissions/${s.id}`} className="text-brand-700 hover:text-brand-800 font-semibold">{s.id}</Link></td>
                  <td className="px-3 py-3"><Link to={`/admin/members/${m?.id}`} className="text-ink font-medium hover:text-brand-700">{m?.firstName} {m?.lastName}</Link></td>
                  <td className="px-3 py-3 text-ink-muted text-caption tabular-nums whitespace-nowrap">{format.relative(s.submittedAt)}</td>
                  <td className="px-3 py-3 text-ink tabular-nums text-right">{s.cardCount}</td>
                  <td className="px-3 py-3 text-ink font-semibold tabular-nums text-right">{format.currency(s.totalFaceValue)}</td>
                  <td className="px-3 py-3"><StatusBadge entity="submission" status={s.status} dot /></td>
                  <td className="px-6 py-3 text-right">
                    <Link to={`/admin/submissions/${s.id}`} className="inline-flex items-center gap-1 text-caption font-semibold text-brand-700 hover:text-brand-800">
                      Review <ChevronRight className="size-3.5" />
                    </Link>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="px-6 py-10 text-center text-caption text-ink-subtle">No matches.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function KV({ icon, label, value, sub, inline = false }) {
  if (inline) {
    return (
      <div className="inline-flex items-center gap-2 text-caption">
        {icon && <span className="text-ink-hint">{icon}</span>}
        <span className="text-ink-hint">{label}:</span>
        <span className="text-ink font-semibold tabular-nums">{value}</span>
      </div>
    );
  }
  return (
    <div>
      <div className="flex items-center gap-1.5 text-caption text-ink-hint mb-1">{icon}{label}</div>
      <div className="text-body-sm text-ink font-semibold tabular-nums">{value}</div>
      {sub && <div className="text-caption text-ink-subtle tabular-nums mt-0.5">{sub}</div>}
    </div>
  );
}

function Stat({ label, value, accent = false }) {
  return (
    <div className="flex justify-between items-baseline">
      <dt className="text-caption text-ink-subtle">{label}</dt>
      <dd className={`text-body-sm font-semibold tabular-nums ${accent ? "text-brand-700" : "text-ink"}`}>{value}</dd>
    </div>
  );
}
